import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  contracts,
  type EmailTemplateArea,
  type MailboxConnectionResponse,
  type MeResponse,
  type Permission,
} from '@immo/api-contract';
import { useToast } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';
import { apiClient } from '@/lib/auth/api-client';
import { mayCall } from '@/lib/permissions/route-gate';
import { imapReconnectOf } from '@/pages/settings/mailbox-connect-flow';
import { MailboxConnectDialog } from '@/pages/workspace/contacts/components/mailbox-connect-dialog';
import { ComposerDock } from '@/pages/workspace/contacts/components/composer/composer-dock';
import { useComposers, type ComposerSeed } from '@/pages/workspace/contacts/components/composer/use-composers';
import { MailboxRequiredDialog } from './mailbox-required-dialog';

const COMPOSE_CONTEXT_DEADLINE_MS = 10_000;

export type ComposeBlock = 'NO_PERMISSION' | 'NOT_CONNECTED' | 'EXPIRED' | 'LOAD_FAILED';

export type ComposeDraft = {
  // Runs only once the mailbox is known to be ready. The provider adds the frozen signature.
  readonly buildSeed: (context: { readonly workspaceId: string }) => Promise<Omit<ComposerSeed, 'signatureHtml'>>;
  // Fires once, after a successful send of this composer, and never on close.
  readonly onSent: () => void;
};

export type ComposeDraftResult =
  | { readonly kind: 'opened' }
  | { readonly kind: 'blocked'; readonly block: ComposeBlock }
  // A previous click is still resolving; this one did nothing.
  | { readonly kind: 'busy' };

export type EmailCompose = {
  readonly composeTo: (address: string, templateArea?: EmailTemplateArea) => void;
  readonly isOwnAddress: (address: string) => boolean;
  readonly canCompose: boolean;
  readonly minimisedCount: number;
  readonly composeDraft: (draft: ComposeDraft) => Promise<ComposeDraftResult>;
  readonly canConnect: boolean;
  readonly connectMailbox: () => void;
};

const EmailComposeContext = createContext<EmailCompose | null>(null);

export function useEmailCompose(): EmailCompose | null {
  return useContext(EmailComposeContext);
}

type ComposeContext = {
  readonly connection: MailboxConnectionResponse;
  readonly signatureHtml: string | null;
};

const normaliseEmail = (value: string) => value.trim().toLowerCase();

async function readSignature(workspaceId: string): Promise<string | null> {
  try {
    const result = await apiClient.companySignature.resolve({ pathParams: { workspaceId }, query: {} });

    return result.ok ? result.body.html : null;
  } catch {
    return null;
  }
}

async function readComposeContext(workspaceId: string): Promise<ComposeContext | null> {
  try {
    const [connection, signatureHtml] = await Promise.all([
      apiClient.mailbox.getConnection({ pathParams: { workspaceId } }),
      readSignature(workspaceId),
    ]);

    return connection.ok ? { connection: connection.body, signatureHtml } : null;
  } catch {
    return null;
  }
}

// The generated client takes no AbortSignal, so the deadline is an explicit timer that
// the settled promise clears — a bare race would leave it pending after every success.
function withDeadline<T>(promise: Promise<T | null>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
    );
  });
}

export type EmailComposeProviderProps = {
  readonly workspaceId: string;
  readonly workspaceSlug: string;
  readonly me: MeResponse;
  readonly permissions: ReadonlyArray<Permission>;
  readonly children: ReactNode;
};

// Keyed by workspace: the layout survives a workspace switch, and drafts, the
// connect prompt and the cached connection must not outlive the scope they
// were loaded for.
export function EmailComposeProvider(props: EmailComposeProviderProps) {
  return <WorkspaceEmailComposeProvider key={props.workspaceId} {...props} />;
}

function WorkspaceEmailComposeProvider({
  workspaceId,
  workspaceSlug,
  me,
  permissions,
  children,
}: EmailComposeProviderProps) {
  const t = useTranslations('pages.workspace.mailboxSurface');
  const toast = useToast();
  const { composers, openComposer, closeComposer, toggleMinimise } = useComposers();
  const [connection, setConnection] = useState<MailboxConnectionResponse | null>(null);
  const [prompt, setPrompt] = useState<'connect' | 'reconnect' | null>(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const resolving = useRef(false);
  const sentCallbacks = useRef(new Map<string, () => void>());

  const canCompose = mayCall(permissions, contracts.mailbox.send);
  const canConnect = mayCall(permissions, contracts.mailbox.connect);
  const ownEmail = normaliseEmail(me.user.email);

  const composeTo = useCallback(
    (address: string, templateArea?: EmailTemplateArea) => {
      if (resolving.current) return;

      resolving.current = true;

      void (async () => {
        try {
          // Read at click time, never cached: a grant can expire mid-session, and the
          // signature seed is frozen into the composer the moment it opens.
          const context = await withDeadline(readComposeContext(workspaceId), COMPOSE_CONTEXT_DEADLINE_MS);

          if (context === null) {
            toast.danger(t('load-error'));

            return;
          }

          setConnection(context.connection);

          if (!context.connection.connected) return setPrompt('connect');

          if (context.connection.status === 'EXPIRED') return setPrompt('reconnect');

          openComposer('new', {
            to: [{ email: address }],
            signatureHtml: context.signatureHtml,
            ...(templateArea !== undefined ? { templateArea } : {}),
          });
        } finally {
          resolving.current = false;
        }
      })();
    },
    [workspaceId, openComposer, t, toast],
  );

  const composeDraft = useCallback(
    async (draft: ComposeDraft): Promise<ComposeDraftResult> => {
      if (!canCompose) return { kind: 'blocked', block: 'NO_PERMISSION' };

      if (resolving.current) return { kind: 'busy' };

      resolving.current = true;

      try {
        const context = await withDeadline(readComposeContext(workspaceId), COMPOSE_CONTEXT_DEADLINE_MS);

        if (context === null) return { kind: 'blocked', block: 'LOAD_FAILED' };

        setConnection(context.connection);

        if (!context.connection.connected) return { kind: 'blocked', block: 'NOT_CONNECTED' };

        if (context.connection.status === 'EXPIRED') return { kind: 'blocked', block: 'EXPIRED' };

        const seed = await withDeadline(draft.buildSeed({ workspaceId }), COMPOSE_CONTEXT_DEADLINE_MS);

        if (seed === null) return { kind: 'blocked', block: 'LOAD_FAILED' };

        const id = openComposer('new', { ...seed, signatureHtml: context.signatureHtml });

        sentCallbacks.current.set(id, draft.onSent);

        return { kind: 'opened' };
      } catch {
        return { kind: 'blocked', block: 'LOAD_FAILED' };
      } finally {
        resolving.current = false;
      }
    },
    [workspaceId, canCompose, openComposer],
  );

  const connectMailbox = useCallback(() => setConnectOpen(true), []);

  const isOwnAddress = useCallback((address: string) => normaliseEmail(address) === ownEmail, [ownEmail]);
  const minimisedCount = composers.filter((composer) => composer.minimised).length;
  const value = useMemo(
    () => ({ composeTo, isOwnAddress, canCompose, minimisedCount, composeDraft, canConnect, connectMailbox }),
    [composeTo, isOwnAddress, canCompose, minimisedCount, composeDraft, canConnect, connectMailbox],
  );

  return (
    <EmailComposeContext.Provider value={value}>
      {children}
      <ComposerDock
        composers={composers}
        workspaceId={workspaceId}
        connectedEmail={connection?.connected === true ? connection.email : null}
        provider={connection?.connected === true ? connection.provider : null}
        onToggleMinimise={toggleMinimise}
        onClose={(id) => {
          sentCallbacks.current.delete(id);
          closeComposer(id);
        }}
        onSent={(id) => {
          sentCallbacks.current.get(id)?.();
          sentCallbacks.current.delete(id);
          closeComposer(id);
        }}
      />
      <MailboxRequiredDialog
        mode={prompt}
        canConnect={canConnect}
        onClose={() => setPrompt(null)}
        onConnect={() => {
          setPrompt(null);
          setConnectOpen(true);
        }}
      />
      <MailboxConnectDialog
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        workspaceId={workspaceId}
        workspaceSlug={workspaceSlug}
        // A connected (expired or switchover) member reconnects on their OWN provider.
        availableProviders={
          connection === null
            ? ['GOOGLE']
            : connection.connected && connection.provider !== null
              ? [connection.provider]
              : connection.availableProviders
        }
        // The connection is re-read on the next compose click, so nothing to reload here.
        onConnected={() => setConnection(null)}
        reconnect={imapReconnectOf(connection)}
      />
    </EmailComposeContext.Provider>
  );
}
